import React, { useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { AppUrls } from 'common/AppUrls'
import { onDeviceConfigChange } from 'store/actions/settings'
import { PageHeaderWithButtons, ModalAsDropdown } from 'components/shared'
import { ReactComponent as TickIcon } from 'assets/svg/settings/tick_blue.svg'
import { VolumeMeter } from './components/VolumeMeter'
import { RingtonePlayer } from './components/RingtonePlayer'
import { SettingBlock } from './components/SettingBlock'
import { CodecsBlock } from './components/CodecsBlock'
import { ModalStyles } from './config/ModalStyles'
import './SoundAndDevices.scss'
import { useTranslation } from 'react-i18next'
import { RingtoneConfig } from 'store/models/Ringtones'

const defaultDropdownImgClassName = 'sound-and-devices__item-btn__img'
const activeClassName = `${defaultDropdownImgClassName}--active`

// TODO: change using useState to useReducer
export const SoundAndDevices = () => {
    const dispatch = useDispatch()
    const soundRef = useRef(null)
    const microphoneRef = useRef(null)
    const speakersCallRef = useRef(null)
    const speakersRingtoneRef = useRef(null)
    const { speakersDevices, microphoneDevices, selectedConfigIds, volume } = useSelector((state) => state.settings)
    const ringtone = RingtoneConfig.find((item) => item.id === selectedConfigIds.ringtoneId)

    // State
    const [modalStyles, setModalStyles] = useState(ModalStyles)
    const [isModalVisible, setModalVisible] = useState(false)
    const [modalContent, setModalContent] = useState([])
    const [ringtonePercent, setRingtonePercent] = useState(0)
    const [isPlayerActive, setPlayerActive] = useState(false)
    const [playerConfigName, setConfigName] = useState(null)

    // Actions
    const toggleModal = (event, ref, config, configName) => {
        if (event) {
            const viewPortWidth = window.innerWidth
            const { y } = event.target.getBoundingClientRect()

            setModalStyles({
                ...modalStyles,
                content: {
                    ...modalStyles.content,
                    top: `${y + 25}px`,
                    left: `${viewPortWidth / 2}px`
                }
            })
        }
        if (ref) {
            classListActionProvider(ref, 'add')
        } else {
            ;[soundRef, microphoneRef, speakersCallRef, speakersRingtoneRef].forEach(
                (ref) => classListActionProvider(ref, 'contains') && classListActionProvider(ref, 'remove')
            )
        }

        setModalContent(renderModalContent(config, configName))
        setModalVisible(!isModalVisible)
    }

    const classListActionProvider = (ref, action) => ref.current.children[0].classList[action](activeClassName)

    const renderModalContent = (modalConfig, configName) => {
        return modalConfig
            ? modalConfig.map(({ id, name, isSelected }) => (
                  <div
                      key={id}
                      className="sound-and-devices__dropdown-item d-flex justify-content-between align-items-center"
                      onClick={() => onDropdownSelect(id, configName)}
                  >
                      <span className="sound-and-devices__dropdown-item__text">{t(name)}</span>
                      {selectedConfigIds[configName] === id && <TickIcon />}
                  </div>
              ))
            : []
    }

    const onDropdownSelect = (id, configName) => {
        dispatch(onDeviceConfigChange(id, configName))
        toggleModal()
        setModalVisible(false)
        switch (configName) {
            case 'ringtoneId':
                startRingtone('speakersCallDeviceId')
                break
            case 'microphoneDeviceId':
                break
            default:
                startRingtone(configName)
                break
        }
    }

    const startRingtone = (configName) => {
        setRingtonePercent(100)
        setPlayerActive(true)
        configName ? setConfigName(configName) : setConfigName(null)
    }

    const closePlayer = () => {
        setPlayerActive(false)
        setConfigName(null)
    }

    const { t } = useTranslation()

    return (
        <>
            <PageHeaderWithButtons backUrl={AppUrls.settings} pageName={t('sound_and_devices')} />
            <SettingBlock
                title={t('sound_notifications')}
                value={selectedConfigIds.ringtoneId}
                fieldText={t('ringtone')}
                icon="ringtone"
                buttonRef={soundRef}
                config={RingtoneConfig}
                configName="ringtoneId"
                toggleModal={toggleModal}
            />
            <SettingBlock
                title={t('speakers')}
                value={selectedConfigIds.speakersCallDeviceId}
                fieldText={t('output')}
                icon="output"
                buttonRef={speakersCallRef}
                config={speakersDevices}
                configName="speakersCallDeviceId"
                toggleModal={toggleModal}
                withOutMarginBottom
            />
            <SettingBlock
                value={selectedConfigIds.speakerRingtoneDeviceId}
                fieldText={t('ringing_device')}
                icon="ringing_device"
                buttonRef={speakersRingtoneRef}
                config={speakersDevices}
                configName="speakerRingtoneDeviceId"
                toggleModal={toggleModal}
            />
            <SettingBlock
                title={t('microphone')}
                value={selectedConfigIds.microphoneDeviceId}
                fieldText={t('microphone')}
                icon="microphone"
                buttonRef={microphoneRef}
                config={microphoneDevices}
                configName="microphoneDeviceId"
                toggleModal={toggleModal}
            >
                <VolumeMeter deviceId={selectedConfigIds.microphoneDeviceId} />
            </SettingBlock>
            <CodecsBlock />
            {isPlayerActive && (
                <RingtonePlayer
                    progressPercent={ringtonePercent}
                    setRingtonePercent={setRingtonePercent}
                    isPlayerActive={isPlayerActive}
                    closePlayer={closePlayer}
                    ringtone={ringtone}
                    deviceId={selectedConfigIds[playerConfigName]}
                    volume={volume}
                />
            )}

            <ModalAsDropdown
                isModalVisible={isModalVisible}
                toggleModal={toggleModal}
                modalContent={() => modalContent}
                customModalStyles={modalStyles}
            />
        </>
    )
}
